'use client';
/**
 * Frame side of the promote-review bridge (spec §3). Renders nothing and is
 * completely inert unless the page URL carries ?_promoteReview=1 — i.e. it is
 * being reviewed inside the admin's compare iframes. Answers only messages
 * whose origin is in NEXT_PUBLIC_PROMOTE_PARENT_ORIGINS.
 */
import { useEffect } from 'react';
import {
  BRIDGE_VERSION, REVIEW_PARAM,
  isParentToFrame, parseAllowedOrigins, isOriginAllowed,
  type FrameToParent,
} from '@/lib/promote/bridgeProtocol';

const HL_STYLE_ID = 'promote-review-hl-style';
const HL_CSS = `
.promote-hl-add { outline: 3px solid #2da44e !important; outline-offset: 3px; }
.promote-hl-del { outline: 3px solid #e5534b !important; outline-offset: 3px; }
`;

const cssEscape = (s: string) =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(s) : s.replace(/"/g, '\\"');

export function PromoteReviewBridge() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get(REVIEW_PARAM) !== '1') return;

    const allowed = parseAllowedOrigins(process.env.NEXT_PUBLIC_PROMOTE_PARENT_ORIGINS);
    let parentOrigin: string | null = null;

    if (!document.getElementById(HL_STYLE_ID)) {
      const style = document.createElement('style');
      style.id = HL_STYLE_ID;
      style.textContent = HL_CSS;
      document.head.appendChild(style);
    }

    const send = (msg: FrameToParent) => {
      if (parentOrigin) window.parent.postMessage(msg, parentOrigin);
    };

    const measureAnchors = () => {
      const els = Array.from(document.querySelectorAll<HTMLElement>('[data-promote-block]'));
      return els.map((el) => {
        const r = el.getBoundingClientRect();
        return {
          blockId: el.getAttribute('data-promote-block') ?? '',
          top: r.top + window.scrollY,
          height: r.height,
        };
      });
    };

    const clearHighlights = () => {
      document.querySelectorAll('.promote-hl-add, .promote-hl-del').forEach((el) => {
        el.classList.remove('promote-hl-add', 'promote-hl-del');
      });
    };

    const onMessage = (e: MessageEvent) => {
      if (!isOriginAllowed(e.origin, allowed)) return;
      if (!isParentToFrame(e.data)) return;
      parentOrigin = e.origin;
      const msg = e.data;
      switch (msg.type) {
        case 'hello':
          send({ v: BRIDGE_VERSION, type: 'ready' });
          break;
        case 'get-anchors':
          send({
            v: BRIDGE_VERSION, type: 'anchors',
            anchors: measureAnchors(),
            docHeight: document.documentElement.scrollHeight,
          });
          break;
        case 'scroll-to': {
          if (msg.blockId) {
            const el = document.querySelector<HTMLElement>(`[data-promote-block="${cssEscape(msg.blockId)}"]`);
            if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            else if (typeof msg.top === 'number' && typeof window.scrollTo === 'function') window.scrollTo({ top: msg.top, behavior: 'smooth' });
          } else if (typeof msg.top === 'number' && typeof window.scrollTo === 'function') {
            window.scrollTo({ top: msg.top, behavior: 'auto' });
          }
          break;
        }
        case 'highlight': {
          clearHighlights();
          const el = document.querySelector<HTMLElement>(`[data-promote-block="${cssEscape(msg.blockId)}"]`);
          if (el) el.classList.add(msg.tone === 'add' ? 'promote-hl-add' : 'promote-hl-del');
          break;
        }
        case 'clear-highlights':
          clearHighlights();
          break;
      }
    };

    let scrollRaf = 0;
    const onScroll = () => {
      if (scrollRaf) return;
      scrollRaf = window.requestAnimationFrame(() => {
        scrollRaf = 0;
        send({
          v: BRIDGE_VERSION, type: 'scrolled',
          top: window.scrollY,
          height: document.documentElement.scrollHeight,
        });
      });
    };

    window.addEventListener('message', onMessage);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('scroll', onScroll);
      if (scrollRaf) window.cancelAnimationFrame(scrollRaf);
    };
  }, []);

  return null;
}
